import { ProcessType } from '../../../../shared/models/process-type';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { Subscription, debounce, debounceTime } from 'rxjs';
import { Process } from '../../../../shared/models/process';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from '../../../../shared/models/subject';
import { ProcessTypeService } from '../../../../shared/services/process-type.service';
import { ProcessService } from '../../../../shared/services/process.service';
import { SubjectService } from '../../../../shared/services/subject.service';
import { ProcessDialogComponent } from '../../../content/processes/process-dialog/process-dialog.component';
import { AuthService } from 'app/shared/services/auth.service';
import { EventService } from 'app/shared/services/event.service';
import { LookupService } from 'app/shared/services/lookup.service';
import { LookupTypeService } from 'app/shared/services/lookup-type.service';

@Component({
  selector: 'checklist-list-widget',
  templateUrl: './checklist-list-widget.component.html',
  styleUrls: ['./checklist-list-widget.component.scss'],
  animations: fuseAnimations,
})
export class ChecklistWidgetComponent implements OnInit, OnDestroy {
  // data is an array of the process records linked to this subject
  _data = [];
  selected = [];
  columns = [];
  _processType: ProcessType;
  _selected: any;
  allowAdd = false;
  allowEdit = false;
  userReadOnly = false;
  admin = false;

  PartnerOrganisationName: string = 'Partner Organisation';

  @Input() set data(val: any[]) {
    this._data = [...val];
  }

  _subject: Subject;
  _processTypeId: number;

  @Input() set subject(val: Subject) {
    this.getProcessesForSubject(val);
  }

  //add inputs to remove errors.
  @Input() id: Number;
  @Input() subjectId: Number;
  @Input() processType: ProcessType;

  @Input() title = 'Completed Checklists';
  @Input() editable = false;
  @Input() titleBackgroundColor: string = '#0277bd';
  @Input() allowDelete = false;
  @Output() onSelect: EventEmitter<any> = new EventEmitter();
  @Output() onEdit: EventEmitter<any> = new EventEmitter();
  @Output() onDelete: EventEmitter<any> = new EventEmitter();

  private onSubjectsChanged: Subscription;
  private onSubjectUpdated: Subscription;
  private onSubjectsServiceChanged:Subscription;
  private onProcessesChanged: Subscription;
  preventSingleClick = true;
  lastClick: Date;

  constructor(
    private processService: ProcessService,
    private subjectService: SubjectService,
    private authService: AuthService,
    private eventService: EventService,
    private changeDetector: ChangeDetectorRef,
    public dialog: MatDialog,
  ) {
    this.ngOnInit= this.ngOnInit.bind(this);
  }

  ngOnInit() { 

    this.onSubjectUpdated = this.eventService.onChecklistRecordAdded.pipe(debounceTime(300)).subscribe((subject) => {
      console.log("Updating Checklist widget")      
      if (this._subject.id==subject.id)
        this.getProcessesForSubject(this._subject);
    });

    this.onSubjectsServiceChanged = this.subjectService.onChanged.subscribe((_subject) => {
      if (this._subject)
        this.getProcessesForSubject(this._subject);
    });  

    this.userReadOnly = this.authService.isUserReadOnly();
    this.admin = this.authService.hasPriv('admin');
  }

  ngOnDestroy() {
    this.onSubjectsChanged?.unsubscribe();
    this.onProcessesChanged?.unsubscribe();
    this.onSubjectUpdated?.unsubscribe();
    this.onSubjectsServiceChanged?.unsubscribe();
  }

  select(row) {
    this._selected = row;

    // javascript can't handle click and dblick click events on the same element, so have to simulate the double click and call the edit popup if we think it's a double click
    if (!this.lastClick) {
      this.lastClick = new Date();
    } else {
      const thisClick = new Date();
      const dif = (thisClick.getTime() - this.lastClick.getTime()) / 1000;
      this.lastClick = new Date();

      if (dif < 0.25) {
        this.edit(row);
      }
    }  
  }

  edit(row?) {
    row = row || this._selected;
    const process = this._data.find((x) => x.id === row.id);
    this.showDialog(process.process);
  }

  showDialog(process: Process) {
    const dialog = this.dialog.open(ProcessDialogComponent, {
      data: process,
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        setTimeout(() => {
          this.getProcessesForSubject();
        }, 500);
      }
    });
  }

  getProcessesForSubject(subject?: Subject, processTypeId?: number) {
    subject = subject || this._subject;
    if (subject) {
      this._subject = subject;
    }

    this.allowEdit = true;
    const processes = [];
    this.allowAdd = false;
    this.allowDelete = false;
    this._data = [];    
    if (subject) {
      this.processService
        .getSubjectCheckLists(subject)
        .subscribe((dataItems) => {
          console.log("New checklist data obtained. Processing into list items", dataItems);
          dataItems?.forEach((p) => {
            processes.push({
              id: p.id,
              uid: p.uid,
              title:
                p.getSummary(p.processType, true) ||
                p.processType.name + ' ' + p.id.toString(),
              secondary: (p.processType.isCheckList === true ? "Completed on " + this.formatDateTime(p.dateTimeCompleted) : ""),
              process: p
            });
          });          
          this._data = [...processes.sort((a, b) => b.id - a.id)];
          this.changeDetector.detectChanges();
        });
    }
    this.changeDetector.detectChanges();
  }

  formatDateTime(dateToFormat: Date) {
    const date = new Date(dateToFormat);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const formattedDateAndTime: string = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    return formattedDateAndTime;
  }
}
